import { DateInput } from '../../../../components/DateInput/DateInput';
import React, { useState, useEffect } from 'react';
import { profileService, UserBankDetailsDto, PreferredBankDto } from '../../../../services/profile.service';
import { toast } from 'react-toastify';
import { validateBankDetails } from '../../../../utils/investorValidation';
import { SharedFormContext } from '../shared/types';
import { useLiveErrorClearing } from '../shared/useLiveErrorClearing';
import { useDelegationPermissions } from '../../../../contexts/DelegationPermissionsContext';
import { getPermissionErrorMessage } from '../../../../utils/apiClient';
import { PremiumSelect } from '../../../../components/PremiumSelect/PremiumSelect';
import { isResidentIndividual, isFieldVisible } from '../../../../config/profileVisibility';

// Dropdown values are UPPERCASE — see convention in shared/constants.ts.
const ACCOUNT_TYPE_OPTIONS = [
  { value: 'SAVINGS', label: 'Savings' },
  { value: 'CURRENT', label: 'Current' },
  { value: 'NRO', label: 'NRO' },
  { value: 'NRE', label: 'NRE' },
];

const COUNTRY_OPTIONS = [
  { value: 'INDIA', label: 'INDIA' },
];

/** Indian states & UTs — Laravel populates this from master_states filtered to India. */
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
  'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];

const STATE_OPTIONS = INDIAN_STATES.map(s => ({ value: s, label: s.toUpperCase() }));

interface BankDetailsFormProps {
  initialData: UserBankDetailsDto | null;
  onSave: () => void;
  canEdit: boolean;
  canSubmit: boolean;
  isProxyMode: boolean;
  sharedContext: SharedFormContext;
}

export const BankDetailsForm: React.FC<BankDetailsFormProps> = ({
  initialData,
  onSave,
  canEdit,
  isProxyMode,
  sharedContext
}) => {
  const delegationPerms = useDelegationPermissions();
  const [formData, setFormData] = useState<Partial<UserBankDetailsDto>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [preferredBank, setPreferredBank] = useState<PreferredBankDto | null>(null);

  // RI profile rules: hide NRE/NRO/PIS block + Country, prefill beneficiary
  // name from registration, and skip the settlement-account-type gate that
  // otherwise blocks the rest of the form.
  const investorType = sharedContext.dashboardData?.investor?.investorType ?? undefined;
  const isRi = isResidentIndividual(investorType);
  const showSettlementQuestion = isFieldVisible(investorType, 'bank.haveNreAccount');
  const showPisBlock = isFieldVisible(investorType, 'bank.rbiApproval');

  // Laravel parity (information-update.blade.php:1556-1566): the account-type label in the settlement
  // question is derived from the PRODUCT name — default "NRO", or "NRE" when the (journey-scoped) product
  // name contains "nre". So an NRE-PIS journey asks "…NRE account?" and an NRO journey asks "…NRO account?".
  const bankAccountTypeLabel =
    /nre/i.test(sharedContext.dashboardData?.productAssignment?.productName ?? '') ? 'NRE' : 'NRO';

  // Laravel parity: the PIS questions show for BOTH NRE and NRO journeys —
  // #pis_account_section (blade:1793) is gated only on settlement=yes, and the
  // PIS-approval wrapper (blade:1953) renders in both branches. Only RI (via
  // profileVisibility) skips PIS entirely; NRE vs NRO changes labels only.
  const pisApplicable = showPisBlock;

  useEffect(() => {
    if (initialData) {
      const hydrated: Partial<UserBankDetailsDto> = { ...initialData };
      // Radios compare exact values — normalize casing from older rows
      // (Laravel stored lowercase yes/no; our radios use YES/NO except hasPisAccount).
      if (hydrated.settlementAccountType) hydrated.settlementAccountType = hydrated.settlementAccountType.toUpperCase();
      if (hydrated.rbiApproval) hydrated.rbiApproval = hydrated.rbiApproval.toUpperCase();
      if (hydrated.hasPisAccount) hydrated.hasPisAccount = hydrated.hasPisAccount.toLowerCase();
      if (isRi) {
        // Beneficiary name defaults to the registered name when empty.
        const dash = sharedContext.dashboardData?.investor;
        const full = [dash?.firstName, dash?.lastName]
          .filter((s) => s && s.trim() !== '')
          .join(' ')
          .trim();
        if ((!hydrated.beneficiaryName || hydrated.beneficiaryName.trim() === '') && full) {
          hydrated.beneficiaryName = full;
        }
        if (!hydrated.bankDetailsCountry) {
          hydrated.bankDetailsCountry = 'INDIA';
        }
      }
      setFormData(hydrated);
    }
  }, [initialData, isRi, sharedContext.dashboardData]);

  // Load broker's preferred bank (Laravel parity) and prefill bankName when empty
  useEffect(() => {
    let cancelled = false;
    profileService.getPreferredBank()
      .then((pb) => {
        if (cancelled || !pb) return;
        setPreferredBank(pb);
        if (pb.isBroker && pb.bankName) {
          setFormData((prev) => ({
            ...prev,
            bankName: prev.bankName && prev.bankName.trim() !== '' ? prev.bankName : pb.bankName ?? '',
          }));
        }
      })
      .catch((err) => {
        // Non-fatal — the form still works without the preferred-bank hint
        console.warn('[BankDetailsForm] failed to load preferred bank:', err);
      });
    return () => { cancelled = true; };
  }, []);

  // Corrected fields drop their error immediately (remove-only live re-check). This form's
  // submit adds inline checks on top of validateBankDetails, so the closure mirrors them;
  // an imperfect mirror is safe under remove-only semantics — worst case a message just
  // stays until Submit, which is the old behaviour.
  useLiveErrorClearing(errors, setErrors, () => {
    const eff = { ...formData };
    if (!eff.bankDetailsCountry?.trim()) eff.bankDetailsCountry = 'INDIA';
    if (pisApplicable && formData.hasPisAccount === 'yes' && !eff.pisBankCountry?.trim()) {
      eff.pisBankCountry = 'INDIA';
    }
    const fresh: Record<string, string> = { ...validateBankDetails(eff) };
    if (!isRi && !formData.settlementAccountType) fresh.settlementAccountType = 'required';
    if (pisApplicable && !formData.rbiApproval) fresh.rbiApproval = 'required';
    if (pisApplicable && !formData.hasPisAccount) fresh.hasPisAccount = 'required';
    if (pisApplicable && formData.hasPisAccount === 'yes') {
      const pisRequired: (keyof UserBankDetailsDto)[] = [
        'pisBeneficiaryName', 'pisBankName', 'pisBranchName', 'pisBankBranchAddress',
        'pisBankAccountNumber', 'pisBankIfscCode', 'pisBankMicr', 'pisBankCountry',
        'pisBankState', 'pisBankCity', 'pisBankZipCode',
      ];
      for (const key of pisRequired) {
        const val = eff[key];
        if (!val || String(val).trim() === '') fresh[key] = 'required';
      }
      if (formData.pisBankMicr && !/^\d{9}$/.test(formData.pisBankMicr)) fresh.pisBankMicr = 'invalid';
      if (eff.pisBankIfscCode
          && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(String(eff.pisBankIfscCode).trim().toUpperCase())) {
        fresh.pisBankIfscCode = 'invalid';
      }
      for (const key of ['pisBeneficiaryName', 'pisBankName', 'pisBranchName'] as (keyof UserBankDetailsDto)[]) {
        const val = eff[key];
        if (val && !/^[a-zA-Z\s]+$/.test(String(val))) fresh[key] = 'invalid';
      }
      if (eff.pisBankAccountNumber && !/^\d+$/.test(String(eff.pisBankAccountNumber).trim())) {
        fresh.pisBankAccountNumber = 'invalid';
      }
      if (eff.pisBankCity && String(eff.pisBankCity).length > 100) fresh.pisBankCity = 'invalid';
      if (eff.pisBankZipCode && !/^[A-Za-z0-9 \-]{3,10}$/.test(String(eff.pisBankZipCode).trim())) {
        fresh.pisBankZipCode = 'invalid';
      }
    }
    return fresh;
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // settlementAccountType is now an explicit radio the user answers (Laravel parity).
    // Require it before submit — except for RI, where the question is hidden
    // because NRE/NRO accounts don't apply.
    if (!isRi && !formData.settlementAccountType) {
      setErrors({ settlementAccountType: `Please answer the ${bankAccountTypeLabel}-account question.` });
      toast.error(`Please answer: Do you already have a ${bankAccountTypeLabel} account?`);
      return;
    }

    // Laravel parity (InnerPageController L3245-3296): answering "No" submits only
    // the answer — no bank fields are required or sent, and the server keeps any
    // previously saved data untouched and skips the Dataverse push.
    let payload: UserBankDetailsDto;
    if (!isRi && formData.settlementAccountType === 'NO') {
      payload = { settlementAccountType: 'NO' };
    } else {
      // WYSIWYG guard (QA OCI-NEW-06, hit locally 2026-09-03): the Country selects
      // DISPLAY 'INDIA' when unset but the value never reached formData — validation
      // then failed on a field the investor saw as filled, and the save never left the
      // browser (or the row silently held null). Validate and submit what is displayed.
      const effectiveForm = { ...formData };
      if (!effectiveForm.bankDetailsCountry?.trim()) {
        effectiveForm.bankDetailsCountry = 'INDIA';
      }
      if (pisApplicable && formData.hasPisAccount === 'yes' && !effectiveForm.pisBankCountry?.trim()) {
        effectiveForm.pisBankCountry = 'INDIA';
      }

      // Every problem on this form is collected into ONE map and reported together.
      //
      // Previously each check called setErrors(...) and RETURNED. Because setErrors replaces
      // the whole map and validateBankDetails (below) never ran, an invalid main-bank field was
      // silently unflagged while any PIS problem existed — QA #12 — and fixing the PIS field
      // then revealed the main error as an apparently "new" one — QA #13. Proven live
      // 2026-09-12: the same garbage IFSC showed no error until the PIS block was completed,
      // then reported "Enter a valid IFSC code". Collect, merge, report once.
      const pisErrors: Record<string, string> = {};
      if (pisApplicable && !formData.rbiApproval) {
        pisErrors.rbiApproval = 'Please answer: Do you have PIS Approval?';
      }
      if (pisApplicable && !formData.hasPisAccount) {
        pisErrors.hasPisAccount = 'Please answer: Do you have a PIS Account?';
      }
      if (pisApplicable && formData.hasPisAccount === 'yes') {
        const pisRequired: [keyof UserBankDetailsDto, string][] = [
          ['pisBeneficiaryName', 'PIS Account Holder Name'],
          ['pisBankName', 'PIS Bank Name'],
          ['pisBranchName', 'PIS Branch Name'],
          ['pisBankBranchAddress', 'PIS Bank Address'],
          ['pisBankAccountNumber', 'PIS Account Number'],
          ['pisBankIfscCode', 'PIS IFSC Code'],
          ['pisBankMicr', 'PIS MICR Number'],
          ['pisBankCountry', 'PIS Country'],
          ['pisBankState', 'PIS State'],
          ['pisBankCity', 'PIS City'],
          ['pisBankZipCode', 'PIS Zip Code'],
        ];
        for (const [key, label] of pisRequired) {
          const val = effectiveForm[key];
          if (!val || String(val).trim() === '') pisErrors[key] = `${label} is required.`;
        }
        if (formData.pisBankMicr && !/^\d{9}$/.test(formData.pisBankMicr)) {
          pisErrors.pisBankMicr = 'PIS MICR must be exactly 9 digits.';
        }
        // Same RBI IFSC rule as the main bank (user 2026-09-08).
        if (effectiveForm.pisBankIfscCode && !pisErrors.pisBankIfscCode
            && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(String(effectiveForm.pisBankIfscCode).trim().toUpperCase())) {
          pisErrors.pisBankIfscCode = 'Enter a valid PIS IFSC code (e.g. ICIC0004321).';
        }
        // Laravel format rules for the PIS block (mirrors the main-bank rules).
        const pisLettersOnly: [keyof UserBankDetailsDto, string][] = [
          ['pisBeneficiaryName', 'PIS Account Holder name'],
          ['pisBankName', 'PIS Bank name'],
          ['pisBranchName', 'PIS Branch name'],
        ];
        for (const [key, label] of pisLettersOnly) {
          const val = effectiveForm[key];
          if (val && !pisErrors[key] && !/^[a-zA-Z\s]+$/.test(String(val))) {
            pisErrors[key] = `${label} must contain only letters and spaces.`;
          }
        }
        if (effectiveForm.pisBankAccountNumber && !pisErrors.pisBankAccountNumber
            && !/^\d+$/.test(String(effectiveForm.pisBankAccountNumber).trim())) {
          pisErrors.pisBankAccountNumber = 'PIS Account number must be numeric.';
        }
        if (effectiveForm.pisBankCity && !pisErrors.pisBankCity
            && String(effectiveForm.pisBankCity).length > 100) {
          pisErrors.pisBankCity = 'PIS City must not exceed 100 characters.';
        }
        if (effectiveForm.pisBankZipCode && !pisErrors.pisBankZipCode
            && !/^[A-Za-z0-9 \-]{3,10}$/.test(String(effectiveForm.pisBankZipCode).trim())) {
          pisErrors.pisBankZipCode = 'PIS Zip code must be 3-10 characters (letters, digits, spaces or hyphens).';
        }
      }

      payload = { ...effectiveForm };

      // Laravel parity: NRE/NRO journeys have no account-type input — the type
      // comes from the product. Only RI picks Savings/Current in the UI.
      if (!isRi) payload.accountType = bankAccountTypeLabel;

      // PIS never applies to RI — drop any stale PIS/RBI answers so the server
      // wipes them.
      if (!pisApplicable) {
        const pisKeys: (keyof UserBankDetailsDto)[] = [
          'rbiApproval', 'rbiApprovalOrderNumber', 'rbiApprovalDate', 'hasPisAccount',
          'pisBeneficiaryName', 'pisBankName', 'pisBranchName', 'pisBankBranchAddress',
          'pisBankAccountNumber', 'pisBankIfscCode', 'pisBankMicr', 'pisBankCountry',
          'pisBankState', 'pisBankCity', 'pisBankZipCode',
        ];
        for (const key of pisKeys) delete payload[key];
      } else if (formData.rbiApproval !== 'YES') {
        delete payload.rbiApprovalOrderNumber;
        delete payload.rbiApprovalDate;
      }

      // Main-bank rules run even when the PIS block has problems, so nothing stays hidden.
      const allErrors: Record<string, string> = { ...validateBankDetails(payload), ...pisErrors };
      if (Object.keys(allErrors).length > 0) {
        setErrors(allErrors);
        // Name the first real problem. The old generic "Please complete the PIS Account
        // details." said nothing about WHICH field was missing (QA #15).
        const firstError = Object.values(allErrors)[0];
        toast.error(firstError ? `Please fix: ${firstError}` : 'Please fix the errors in the form');
        return;
      }
    }

    setErrors({});
    setSaving(true);

    try {
      await profileService.updateBankDetails(payload);
      toast.success('Bank details updated');
      onSave();
    } catch (err: any) {
      const permissionError = getPermissionErrorMessage(err);
      if (permissionError) {
        toast.error(permissionError);
      } else {
        toast.error(err.response?.data?.message || 'Failed to update');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="investor-profile__card" onSubmit={handleSubmit}>
      {/* <h3>Bank Details</h3> */}
      {showSettlementQuestion && (
        <div className="investor-profile__grid">
          {/* Laravel parity: settlement_account_type — gate the bank fields on this answer.
              When the broker has a preferred bank configured, the question names the bank
              (matches Laravel: "Do you already have a NRE account with {bank}?").
              RI hides this entirely — NRE/NRO accounts don't apply. */}
          <div className="form-group form-group--full">
            <label>
              {preferredBank?.isBroker && preferredBank.bankName
                ? `Do you already have a ${bankAccountTypeLabel} account with ${preferredBank.bankName}?`
                : `Do you already have a ${bankAccountTypeLabel} account?`}
              {' '}<span className="text-danger">*</span>
            </label>
            <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.4rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'normal' }}>
                <input
                  type="radio"
                  name="settlementAccountType"
                  value="YES"
                  checked={formData.settlementAccountType === 'YES'}
                  onChange={() => setFormData({ ...formData, settlementAccountType: 'YES' })}
                /> Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'normal' }}>
                <input
                  type="radio"
                  name="settlementAccountType"
                  value="NO"
                  checked={formData.settlementAccountType === 'NO'}
                  onChange={() => setFormData({ ...formData, settlementAccountType: 'NO' })}
                /> No
              </label>
            </div>
            {errors.settlementAccountType && <div className="invalid-feedback">{errors.settlementAccountType}</div>}
          </div>
        </div>
      )}

      {/* Laravel equivalent of #show_account_type_div — the bank fields only apply
          when the investor answers Yes; on No the whole section stays hidden and
          only the answer is saved. For RI the question is hidden, so the fields
          render unconditionally. */}
      {(isRi || formData.settlementAccountType === 'YES') && (
        <div className="investor-profile__grid">
          {/* Laravel Section5 field order (blade:1595-1790): holder / bank / branch,
              address / account no / IFSC, MICR / country / state, city / zip. */}
          <div className="form-group">
            <label>Account Holder's Name <span className="text-danger">*</span></label>
            <input
              value={formData.beneficiaryName ?? ''}
              onChange={(e) => setFormData({ ...formData, beneficiaryName: e.target.value })}
              className={errors.beneficiaryName ? 'form-control is-invalid' : ''}
            />
            {errors.beneficiaryName && <div className="invalid-feedback">{errors.beneficiaryName}</div>}
          </div>

          <div className="form-group">
            <label>Bank Name <span className="text-danger">*</span></label>
            <input
              value={formData.bankName ?? ''}
              onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
              className={errors.bankName ? 'form-control is-invalid' : ''}
            />
            {errors.bankName && <div className="invalid-feedback">{errors.bankName}</div>}
          </div>

          <div className="form-group">
            <label>Branch name <span className="text-danger">*</span></label>
            <input
              value={formData.branchName ?? ''}
              onChange={(e) => setFormData({ ...formData, branchName: e.target.value })}
              className={errors.branchName ? 'form-control is-invalid' : ''}
            />
            {errors.branchName && <div className="invalid-feedback">{errors.branchName}</div>}
          </div>

          <div className="form-group">
            <label>Bank Address (Branch Address) <span className="text-danger">*</span></label>
            <input
              value={formData.bankBranchAddress ?? ''}
              onChange={(e) => setFormData({ ...formData, bankBranchAddress: e.target.value })}
              className={errors.bankBranchAddress ? 'form-control is-invalid' : ''}
            />
            {errors.bankBranchAddress && <div className="invalid-feedback">{errors.bankBranchAddress}</div>}
          </div>

          <div className="form-group">
            <label>Bank Account Number <span className="text-danger">*</span></label>
            <input
              value={formData.bankAccountNumber ?? ''}
              onChange={(e) => setFormData({ ...formData, bankAccountNumber: e.target.value })}
              className={errors.bankAccountNumber ? 'form-control is-invalid' : ''}
            />
            {errors.bankAccountNumber && <div className="invalid-feedback">{errors.bankAccountNumber}</div>}
          </div>

          <div className="form-group">
            <label>IFSC Code <span className="text-danger">*</span></label>
            <input
              value={formData.bankIfscCode ?? ''}
              onChange={(e) => setFormData({ ...formData, bankIfscCode: e.target.value })}
              placeholder="e.g., SBIN0001234"
              className={errors.bankIfscCode ? 'form-control is-invalid' : ''}
            />
            {errors.bankIfscCode && <div className="invalid-feedback">{errors.bankIfscCode}</div>}
          </div>

          <div className="form-group">
            <label>MICR No. <span className="text-danger">*</span></label>
            <input
              value={formData.bankDetailsMicr ?? ''}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 9);
                setFormData({ ...formData, bankDetailsMicr: v });
              }}
              placeholder="Enter 9-digit MICR Number"
              maxLength={9}
              pattern="\d{9}"
              inputMode="numeric"
              className={errors.bankDetailsMicr ? 'form-control is-invalid' : 'form-control'}
            />
            {errors.bankDetailsMicr && <div className="invalid-feedback">{errors.bankDetailsMicr}</div>}
          </div>

          <div className="form-group">
            <label>Country <span className="text-danger">*</span></label>
            <PremiumSelect
              value={formData.bankDetailsCountry ?? 'INDIA'}
              onChange={(val) => setFormData({ ...formData, bankDetailsCountry: val })}
              options={COUNTRY_OPTIONS}
              error={errors.bankDetailsCountry}
              searchable
            />
          </div>

          <div className="form-group">
            <label>State <span className="text-danger">*</span></label>
            <PremiumSelect
              value={formData.bankDetailsState ?? ''}
              onChange={(val) => setFormData({ ...formData, bankDetailsState: val })}
              options={STATE_OPTIONS}
              error={errors.bankDetailsState}
              searchable
            />
          </div>

          <div className="form-group">
            <label>City <span className="text-danger">*</span></label>
            <input
              value={formData.bankDetailsCity ?? ''}
              onChange={(e) => setFormData({ ...formData, bankDetailsCity: e.target.value })}
              className={errors.bankDetailsCity ? 'form-control is-invalid' : 'form-control'}
            />
            {errors.bankDetailsCity && <div className="invalid-feedback">{errors.bankDetailsCity}</div>}
          </div>

          <div className="form-group">
            <label>Postal / Zip code <span className="text-danger">*</span></label>
            <input
              value={formData.bankDetailsZipCode ?? ''}
              onChange={(e) => setFormData({ ...formData, bankDetailsZipCode: e.target.value })}
              className={errors.bankDetailsZipCode ? 'form-control is-invalid' : 'form-control'}
            />
            {errors.bankDetailsZipCode && <div className="invalid-feedback">{errors.bankDetailsZipCode}</div>}
          </div>

          {/* RI only: Savings/Current selector. NRE/NRO journeys have no account-type
              input — Laravel's type_of_account is never user-entered; we stamp it from
              the product-derived label on submit. */}
          {isRi && (
            <div className="form-group">
              <label>Account Type <span className="text-danger">*</span></label>
              <PremiumSelect
                value={formData.accountType ?? ''}
                onChange={(val) => setFormData({ ...formData, accountType: val })}
                options={ACCOUNT_TYPE_OPTIONS.filter(o => o.value !== 'NRE' && o.value !== 'NRO')}
                error={errors.accountType}
              />
            </div>
          )}

          {/* ===== PIS ACCOUNT SECTION (blade:1792-1949) — both NRE and NRO journeys ===== */}
          {pisApplicable && (
            <>
              <div className="form-group form-group--full">
                <label>Do you have a PIS Account? <span className="text-danger">*</span></label>
                <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.4rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'normal' }}>
                    <input
                      type="radio"
                      name="hasPisAccount"
                      value="yes"
                      checked={formData.hasPisAccount === 'yes'}
                      onChange={() => setFormData({
                        ...formData,
                        hasPisAccount: 'yes',
                        // Only the account HOLDER is carried over — it is the same person by
                        // definition. The bank name is NOT copied any more (user 2026-09-12):
                        // the PIS account is often with a different bank, and a pre-filled name
                        // reads as fact rather than a guess, so it got submitted unchecked.
                        // Laravel copies both (blade:4078-4087); this is a deliberate divergence.
                        pisBeneficiaryName: formData.pisBeneficiaryName || formData.beneficiaryName,
                      })}
                    /> Yes
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'normal' }}>
                    <input
                      type="radio"
                      name="hasPisAccount"
                      value="no"
                      checked={formData.hasPisAccount === 'no'}
                      onChange={() => setFormData({ ...formData, hasPisAccount: 'no' })}
                    /> No
                  </label>
                </div>
                {errors.hasPisAccount && <div className="text-danger small mt-1">{errors.hasPisAccount}</div>}
              </div>

              {formData.hasPisAccount === 'yes' && (
                <>
                  <div className="form-group">
                    <label>Account Holder's Name <span className="text-danger">*</span></label>
                    <input
                      value={formData.pisBeneficiaryName ?? ''}
                      onChange={(e) => setFormData({ ...formData, pisBeneficiaryName: e.target.value })}
                      className={errors.pisBeneficiaryName ? 'form-control is-invalid' : ''}
                    />
                    {errors.pisBeneficiaryName && <div className="invalid-feedback">{errors.pisBeneficiaryName}</div>}
                  </div>
                  <div className="form-group">
                    <label>Bank Name <span className="text-danger">*</span></label>
                    <input
                      value={formData.pisBankName ?? ''}
                      onChange={(e) => setFormData({ ...formData, pisBankName: e.target.value })}
                      className={errors.pisBankName ? 'form-control is-invalid' : ''}
                    />
                    {errors.pisBankName && <div className="invalid-feedback">{errors.pisBankName}</div>}
                  </div>
                  <div className="form-group">
                    <label>Branch Name <span className="text-danger">*</span></label>
                    <input
                      value={formData.pisBranchName ?? ''}
                      onChange={(e) => setFormData({ ...formData, pisBranchName: e.target.value })}
                      className={errors.pisBranchName ? 'form-control is-invalid' : ''}
                    />
                    {errors.pisBranchName && <div className="invalid-feedback">{errors.pisBranchName}</div>}
                  </div>
                  <div className="form-group">
                    <label>Bank Address (Branch Address) <span className="text-danger">*</span></label>
                    <input
                      value={formData.pisBankBranchAddress ?? ''}
                      onChange={(e) => setFormData({ ...formData, pisBankBranchAddress: e.target.value })}
                      className={errors.pisBankBranchAddress ? 'form-control is-invalid' : ''}
                    />
                    {errors.pisBankBranchAddress && <div className="invalid-feedback">{errors.pisBankBranchAddress}</div>}
                  </div>
                  <div className="form-group">
                    <label>PIS Account Number <span className="text-danger">*</span></label>
                    <input
                      value={formData.pisBankAccountNumber ?? ''}
                      onChange={(e) => setFormData({ ...formData, pisBankAccountNumber: e.target.value })}
                      className={errors.pisBankAccountNumber ? 'form-control is-invalid' : ''}
                    />
                    {errors.pisBankAccountNumber && <div className="invalid-feedback">{errors.pisBankAccountNumber}</div>}
                  </div>
                  <div className="form-group">
                    <label>IFSC Code <span className="text-danger">*</span></label>
                    <input
                      value={formData.pisBankIfscCode ?? ''}
                      onChange={(e) => setFormData({ ...formData, pisBankIfscCode: e.target.value })}
                      placeholder="e.g., SBIN0001234"
                      className={errors.pisBankIfscCode ? 'form-control is-invalid' : ''}
                    />
                    {errors.pisBankIfscCode && <div className="invalid-feedback">{errors.pisBankIfscCode}</div>}
                  </div>
                  <div className="form-group">
                    <label>MICR No. <span className="text-danger">*</span></label>
                    <input
                      value={formData.pisBankMicr ?? ''}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 9);
                        setFormData({ ...formData, pisBankMicr: v });
                      }}
                      placeholder="Enter 9-digit MICR Number"
                      maxLength={9}
                      inputMode="numeric"
                      className={errors.pisBankMicr ? 'form-control is-invalid' : 'form-control'}
                    />
                    {errors.pisBankMicr && <div className="invalid-feedback">{errors.pisBankMicr}</div>}
                  </div>
                  <div className="form-group">
                    <label>Country <span className="text-danger">*</span></label>
                    <PremiumSelect
                      value={formData.pisBankCountry ?? 'INDIA'}
                      onChange={(val) => setFormData({ ...formData, pisBankCountry: val })}
                      options={COUNTRY_OPTIONS}
                      error={errors.pisBankCountry}
                      searchable
                    />
                  </div>
                  <div className="form-group">
                    <label>State <span className="text-danger">*</span></label>
                    <PremiumSelect
                      value={formData.pisBankState ?? ''}
                      onChange={(val) => setFormData({ ...formData, pisBankState: val })}
                      options={STATE_OPTIONS}
                      error={errors.pisBankState}
                      searchable
                    />
                  </div>
                  <div className="form-group">
                    <label>City <span className="text-danger">*</span></label>
                    <input
                      value={formData.pisBankCity ?? ''}
                      onChange={(e) => setFormData({ ...formData, pisBankCity: e.target.value })}
                      className={errors.pisBankCity ? 'form-control is-invalid' : 'form-control'}
                    />
                    {errors.pisBankCity && <div className="invalid-feedback">{errors.pisBankCity}</div>}
                  </div>
                  <div className="form-group">
                    <label>Postal / Zip Code <span className="text-danger">*</span></label>
                    <input
                      value={formData.pisBankZipCode ?? ''}
                      onChange={(e) => setFormData({ ...formData, pisBankZipCode: e.target.value })}
                      className={errors.pisBankZipCode ? 'form-control is-invalid' : 'form-control'}
                    />
                    {errors.pisBankZipCode && <div className="invalid-feedback">{errors.pisBankZipCode}</div>}
                  </div>
                </>
              )}

              {/* PIS approval (blade:1951-2008) — last, after the PIS-account block;
                  number/date appear inline when the answer is Yes. */}
              <div className="form-group">
                <label>Do you have PIS approval? <span className="text-danger">*</span></label>
                <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.4rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'normal' }}>
                    <input
                      type="radio"
                      name="rbiApproval"
                      value="YES"
                      checked={formData.rbiApproval === 'YES'}
                      onChange={() => setFormData({ ...formData, rbiApproval: 'YES' })}
                    /> Yes
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'normal' }}>
                    <input
                      type="radio"
                      name="rbiApproval"
                      value="NO"
                      checked={formData.rbiApproval === 'NO'}
                      onChange={() => setFormData({ ...formData, rbiApproval: 'NO' })}
                    /> No
                  </label>
                </div>
                {errors.rbiApproval && <div className="text-danger small mt-1">{errors.rbiApproval}</div>}
              </div>

              {formData.rbiApproval === 'YES' && (
                <>
                  <div className="form-group">
                    <label>PIS Approval Number <span className="text-danger">*</span></label>
                    <input
                      value={formData.rbiApprovalOrderNumber ?? ''}
                      onChange={(e) => setFormData({ ...formData, rbiApprovalOrderNumber: e.target.value })}
                      placeholder="e.g., PIS/2024/001"
                      className={errors.rbiApprovalOrderNumber ? 'form-control is-invalid' : ''}
                    />
                    {errors.rbiApprovalOrderNumber && <div className="invalid-feedback">{errors.rbiApprovalOrderNumber}</div>}
                  </div>
                  <div className="form-group">
                    <label>PIS Approval Date <span className="text-danger">*</span></label>
                    <DateInput
                      value={formData.rbiApprovalDate ?? ''}
                      onChange={(value) => setFormData({ ...formData, rbiApprovalDate: value })}
                      className={errors.rbiApprovalDate ? 'form-control is-invalid' : ''}
                    />
                    {errors.rbiApprovalDate && <div className="invalid-feedback d-block">{errors.rbiApprovalDate}</div>}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {!sharedContext.lockedAfterSubmit && (
        <button type="submit" className="btn-save" disabled={saving || !canEdit}>
        {saving ? 'Submitting...' : 'Submit'}
        </button>
      )}
      {!canEdit && delegationPerms.isProxyMode && (
        <small className="text-warning d-block mt-2">
          You don't have permission to edit KYC information. Contact the investor to update delegation permissions.
        </small>
      )}
    </form>
  );
};
